import {IQuestionData} from '../definition/question-data';
import {IAttachment, IQuickScanAnswerData, ITisAnswerValue} from '../project/project-data';
import {ProjectAnalysisAnswer} from './index';


export class ProjectAnalysisAnswerEntry {
    public id?: number;
    public questionId: number;
    public answerId?: number;
    public answerAsText?: string;
    public answerAsDate?: string;
    public answerAsInteger?: number;
    public answerAsBool?: boolean;
    public answerAsPercentage?: number;
    public explanation?: string;
    public colorScore?: string;
    public attachments: IAttachment[];
    public pitTag?: string;

    constructor(data: ProjectAnalysisAnswer) {
        this.id = data.id;
        this.questionId = data.questionId;
        this.answerId = data.answerId;
        this.answerAsText = data.answerAsText;
        this.answerAsDate = data.answerAsDate;
        this.answerAsInteger = data.answerAsInteger;
        this.answerAsBool = data.answerAsBool;
        this.answerAsPercentage = data.answerAsPercentage;
        this.explanation = data.explanation;
        this.colorScore = data.colorScore;
        this.attachments = data.attachments ?? [];
        this.pitTag = data.pitTag;
    }

    public get remarks(): string | undefined {
        return this.explanation;
    }

    public setAnswerId(id: number | undefined): void {
        this.answerId = id;
    }

    public setRemarks(remarks: string): void {
        this.explanation = remarks;
    }

    /**
     * Attachments are deliberately not copied here (mirrors the legacy
     * `updateTisAnswer` behaviour); they are managed via `setAttachments`.
     */
    public setValue(value: ITisAnswerValue): void {
        this.answerAsText = value.text;
        this.answerAsDate = value.date == null ? undefined : ProjectAnalysisAnswerEntry.toIsoDate(value.date);
        this.answerAsInteger = value.integer;
        this.answerAsBool = value.bool;
        this.answerAsPercentage = value.percentage;
        this.answerId = value.answerId;
    }

    public setAttachments(attachments: IAttachment[]): void {
        this.attachments = attachments;
    }

    /**
     * Entries are created on first access (also by read paths of the shared
     * quickscan components); unanswered ones must not be persisted.
     * Note: `false` and `0` are real answers, only null/undefined/'' are not.
     */
    public isEmpty(): boolean {
        return this.answerId == null
            && (this.answerAsText == null || this.answerAsText === '')
            && this.answerAsDate == null
            && this.answerAsInteger == null
            && this.answerAsBool == null
            && this.answerAsPercentage == null
            && (this.explanation == null || this.explanation === '')
            && this.attachments.length === 0;
    }

    public getValue(): ITisAnswerValue {
        return {
            text: this.answerAsText,
            date: this.answerAsDate as unknown as Date,
            integer: this.answerAsInteger,
            bool: this.answerAsBool,
            percentage: this.answerAsPercentage,
            answerId: this.answerId,
            attachments: this.attachments
        };
    }

    public toData(): ProjectAnalysisAnswer {
        return {
            id: this.id,
            questionId: this.questionId,
            answerId: this.answerId,
            answerAsText: this.answerAsText,
            answerAsDate: this.answerAsDate,
            answerAsInteger: this.answerAsInteger,
            answerAsBool: this.answerAsBool,
            answerAsPercentage: this.answerAsPercentage,
            explanation: this.explanation,
            colorScore: this.colorScore,
            attachments: this.attachments
        };
    }

    private static toIsoDate(date: Date | string): string {
        if (typeof date === 'string') {
            return date;
        }
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
    }
}

/**
 * Answer store for projects that run on a project analysis definition.
 *
 * Mirrors the `QuickscanAnswers` API (getQuickscanAnswerForQuestion,
 * updateQuickscanAnswer, getQuickscanAnswers) so the shared quickscan
 * components accept either store, and the `updateTisAnswer`-style API used by
 * the TIS question flow.
 */
export class ProjectAnalysisAnswers {
    private answers: ProjectAnalysisAnswerEntry[];

    /**
     * Builds a store from a sessionStorage draft, dropping corrupted values
     * (non numeric ids, integers outside the backend int32 range).
     */
    public static fromDraft(draft: ProjectAnalysisAnswer[]): ProjectAnalysisAnswers {
        // Bitwise ops coerce to int32, so a value is an int32 iff it survives the round trip
        const isInt32 = (value: unknown): value is number =>
            typeof value === 'number' && (value | 0) === value;

        const entries = draft
            .filter((entry) => typeof entry.questionId === 'number')
            .map((entry) => ({
                ...entry,
                answerId: typeof entry.answerId === 'number' ? entry.answerId : undefined,
                answerAsInteger: isInt32(entry.answerAsInteger) ? entry.answerAsInteger : undefined,
                attachments: entry.attachments ?? []
            }));

        return new ProjectAnalysisAnswers(entries);
    }

    constructor(data: ProjectAnalysisAnswer[] = []) {
        // Empty rows may still come from the backend (saved before the
        // frontend stopped sending them); drop them on load as well.
        this.answers = data
            .map((answer) => new ProjectAnalysisAnswerEntry(answer))
            .filter((entry) => !entry.isEmpty());
    }

    public getAnswerForQuestion(question: IQuestionData): ProjectAnalysisAnswerEntry {
        let answer = this.answers.find((entry) => entry.questionId === question.id);

        if (answer === undefined) {
            answer = new ProjectAnalysisAnswerEntry({questionId: question.id, attachments: []});
            this.answers.push(answer);
        }

        return answer;
    }

    public getAnswerByPitTag(pitTag: string): ProjectAnalysisAnswerEntry | undefined {
        const answer = this.answers.find((entry) => entry.pitTag === pitTag);
        if (answer !== undefined) {
            return new ProjectAnalysisAnswerEntry(answer);
        }
        return undefined;
    }

    public getQuickscanAnswerForQuestion(question: IQuestionData): ProjectAnalysisAnswerEntry {
        return this.getAnswerForQuestion(question);
    }

    public updateQuickscanAnswer(question: IQuestionData, selectedAnswerId: number | undefined): void {
        this.getAnswerForQuestion(question).setAnswerId(selectedAnswerId);
    }

    public getQuickscanAnswers(): IQuickScanAnswerData[] {
        return this.answers
            .filter((entry) => !entry.isEmpty())
            .map((entry) => ({
                questionId: entry.questionId,
                answerId: entry.answerId,
                remarks: entry.explanation
            }));
    }

    public getValueForQuestion(question: IQuestionData): ITisAnswerValue {
        return this.getAnswerForQuestion(question).getValue();
    }

    public updateAnswer(question: IQuestionData, value: ITisAnswerValue): void {
        this.getAnswerForQuestion(question).setValue(value);
    }

    public updateAttachments(question: IQuestionData, attachments: IAttachment[]): void {
        this.getAnswerForQuestion(question).setAttachments(attachments);
    }

    public getAnswerIdsForQuestion(question: IQuestionData): number[] {
        return this.answers
            .filter((entry) => entry.questionId === question.id && entry.answerId != null)
            .map((entry) => entry.answerId);
    }

    /**
     * Multi-answer questions are stored as one answer entry per selected
     * possible answer (the backend scheme allows multiple rows per question).
     */
    public updateMultiAnswer(question: IQuestionData, selectedAnswerIds: number[]): void {
        this.answers = this.answers.filter((entry) =>
            entry.questionId !== question.id
            || entry.answerId == null
            || selectedAnswerIds.includes(entry.answerId)
        );

        const existingIds = this.getAnswerIdsForQuestion(question);
        selectedAnswerIds
            .filter((answerId) => !existingIds.includes(answerId))
            .forEach((answerId) => {
                const entry = new ProjectAnalysisAnswerEntry({questionId: question.id, answerId, attachments: []});
                this.answers.push(entry);
            });
    }

    public getAnswers(): ProjectAnalysisAnswer[] {
        return this.answers
            .filter((entry) => !entry.isEmpty())
            .map((entry) => entry.toData());
    }
}
